import * as React from "react";
import { cn, getInitialsFromEmployee } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Employee,
  useEmployeeAttendeeRecommendationsQuery,
} from "@common/utils/graphql";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useState } from "react";
import { useMap } from "usehooks-ts";
import { Check, Plus, PlusCircle, X } from "@phosphor-icons/react";

interface Props {
  defaultValues: string[];
  isSingleSelect?: boolean;
  placeholder: string;
  onChange: (emails: Employee[]) => void;
  allowManualUsers?: boolean;
}

const emptyEmployees: Employee[] = [];

export const SelectAnyEmployeeForm = ({
  onChange,
  isSingleSelect,
  defaultValues,
  placeholder,
  allowManualUsers,
}: Props) => {
  const [searchText, setSearchText] = useState("");
  const [workEmailToMetadata, workEmailToMetadataActions] = useMap<
    string,
    Employee
  >(new Map());

  const { data, loading } = useEmployeeAttendeeRecommendationsQuery({
    variables: {
      offset: 0,
      pageSize: 3,
      searchText:
        0 <= searchText.length && searchText.length < 3 ? null : searchText,
    },
  });

  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Employee[]>(
    (defaultValues || []).map((v) => ({
      workEmail: v,
      firstName: v,
      lastName: "",
    })),
  );

  const changeList = (newList: Employee[]) => {
    onChange(newList);
    setSelected(newList);
  };

  let employees = emptyEmployees;
  if (
    data?.employeeAttendeeRecommendations?.__typename ===
    "QueryEmployeeAttendeeRecommendationsSuccess"
  ) {
    employees = data.employeeAttendeeRecommendations.data.employees;
  }

  React.useEffect(() => {
    if (employees.length > 0 && workEmailToMetadata.size === 0) {
      employees.forEach((employee) =>
        workEmailToMetadataActions.set(employee.workEmail, employee),
      );
    }
  }, [employees, workEmailToMetadataActions, workEmailToMetadata]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-none bg-[#1c1c1c] text-[#a1a1aa]"
          >
            {isSingleSelect
              ? selected.length > 0
                ? selected[0].firstName + " " + selected[0].lastName
                : placeholder
              : placeholder}
            {isSingleSelect && selected.length > 0 ? (
              <X
                className="ml-2 size-4 shrink-0 opacity-50"
                onClick={() => changeList([])}
              />
            ) : (
              <Plus className="ml-2 size-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" className="w-[335px] p-0">
          <Command>
            <CommandInput
              onValueChange={setSearchText}
              value={searchText}
              placeholder="Search employees"
            />
            <CommandEmpty>
              <div>
                {loading ? (
                  <p>Searching...</p>
                ) : 0 < searchText.length && searchText.length < 3 ? (
                  <p>Type in at least three characters to search</p>
                ) : (
                  <div>
                    <p>No employees found</p>
                    {allowManualUsers && (
                      <div className="m-3 flex justify-center pt-4">
                        <div
                          className="flex cursor-pointer flex-row items-center justify-center"
                          onClick={() => {
                            changeList([
                              ...selected,
                              {
                                firstName: "",
                                lastName: "",
                                workEmail: searchText,
                              },
                            ]);
                            setOpen(false);
                          }}
                        >
                          <div>
                            <PlusCircle className={cn("w-100 mr-2 size-4")} />
                          </div>
                          <div className="flex flex-col pl-2">
                            <div>Add {searchText} manually</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {searchText.length < 3 && employees.length > 0 && (
                <p className="p-3 text-center text-sm">Recommended users</p>
              )}
              {searchText.length >= 0 &&
                employees.map((employee) => (
                  <CommandItem
                    key={employee.workEmail}
                    value={employee.workEmail}
                    onSelect={(email) => {
                      const alreadyExists = selected.find(
                        (employee) => employee.workEmail === email,
                      );
                      if (alreadyExists) {
                        if (isSingleSelect) {
                          changeList([]);
                        } else {
                          changeList(
                            selected.filter(
                              (employee) => employee.workEmail !== email,
                            ),
                          );
                        }
                      } else {
                        const meta = workEmailToMetadata.get(email);
                        const newUser = {
                          firstName: meta?.firstName || "",
                          lastName: meta?.lastName || "",
                          workEmail: email,
                        };
                        if (isSingleSelect) {
                          changeList([newUser]);
                        } else {
                          changeList([...selected, newUser]);
                        }
                      }
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full justify-between">
                      <div className="flex flex-row">
                        <Avatar>
                          <AvatarFallback>
                            {getInitialsFromEmployee(employee)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col pl-2">
                          <div>
                            {employee.firstName} {employee.lastName}
                          </div>
                          <div>{employee.workEmail}</div>
                        </div>
                      </div>
                      <div className="flex-end flex items-center justify-end">
                        <Check
                          className={cn(
                            "w-100 mr-2 size-4",
                            selected.find(
                              (e) => e.workEmail === employee.workEmail,
                            )
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                      </div>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      <div>
        {!isSingleSelect &&
          selected.map((selectedEmployee) => {
            return (
              <div
                className="flex w-full justify-between pt-4"
                key={selectedEmployee.id}
              >
                <div className="flex flex-row items-center">
                  <Avatar>
                    <AvatarFallback>
                      {getInitialsFromEmployee(selectedEmployee)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col pl-2">
                    <div>
                      {selectedEmployee.firstName} {selectedEmployee.lastName}
                    </div>
                    <p className="text-slate">{selectedEmployee.workEmail}</p>
                  </div>
                </div>
                <div className="flex-end flex items-center justify-end">
                  <X
                    onClick={() =>
                      changeList(
                        selected.filter(
                          (employee) =>
                            employee.workEmail !== selectedEmployee.workEmail,
                        ),
                      )
                    }
                    className={cn(
                      "w-100 mr-2 h-4 w-4 cursor-pointer opacity-100",
                    )}
                  />
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};
